<template>
  <div class="searchbar">
    <div class="is-flex is-align-items-center" :class="{ 'px-6': showHint }">
      <!-- Search Input -->
      <div style="position: relative; width: 100%">
        <b-input
          ref="searchInput"
          icon="search"
          placeholder="Analyse suchen..."
          v-model="keyword"
          icon-clickable
          expanded
          :class="{ 'border-bottom-flat': showResult }"
          @icon-click="toSearchPage"
          @keyup.native.enter="onSearchEnter"
          @keydown.native.up="selectPrev"
          @keydown.native.down="selectNext"
          @focus="onFocus"
          @blur="onBlur"
          @input="onInput"
        />
        <b-dropdown
          v-model="selectedProduct"
          position="is-bottom-left"
          style="position: absolute; top: 5px; right: 8px"
        >
          <template #trigger="{ active }">
            <b-tooltip
              :label="selectedProduct.name"
              :position="tooltipPos"
              type="is-light"
            >
              <b-button
                type="is-gold"
                size="is-small"
                :icon-left="selectedProduct.icon"
                :icon-right="active ? 'angle-up' : 'angle-down'"
              />
            </b-tooltip>
          </template>

          <b-dropdown-item
            v-for="product in products"
            :key="product.id"
            :value="product"
          >
            <div class="media">
              <b-icon class="media-left" :icon="product.icon"></b-icon>
              <div class="media-content">
                <h3>{{ product.name }}</h3>
              </div>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <!-- End of search input -->

      <!-- Info Tooltip -->
      <b-button
        v-if="showHint"
        icon-left="info"
        size="is-small"
        style="border: none"
        class="ml-2"
        @click="isSearchInfoOpen = true"
      />
      <b-modal v-model="isSearchInfoOpen" has-modal-card>
        <div class="modal-card">
          <section class="modal-card-body">
            <div class="block">
              Die Analyse-Suche befindet sich aktuell in einer Beta-Phase und
              verbessert sich kontinuierlich.
            </div>
            <div class="block">
              Mithilfe der Suchfunktion finden Sie schnell und unkompliziert
              benötigte Kennzahlen und Analysen. Wählen Sie hierfür zunächst das
              Produkt aus, in welchem gesucht werden soll und geben anschließend
              beispielsweise „Zeitreihen“, „Beschäftigte“ und „Teilmärkte“ ein,
              um direkt zu den Zeitreihenanalysen zur Anzahl der Beschäftigten
              auf der Teilmarkt-Ebene zu gelangen.
            </div>
            <div class="block has-text-weight-bold">
              Bitte beachten Sie, dass die Suchfunktion nicht in der Lage ist
              nach Dimensionen, wie zum Beispiel bestimmten Jahren (bspw.
              „2019“) oder spezifischen Teilmärkten (bspw. „Filmmarkt“) zu
              suchen.
            </div>
          </section>
        </div>
      </b-modal>
      <!-- End of Info Tooltip -->
    </div>

    <!-- Search result -->
    <div style="position: relative">
      <slot
        name="result"
        :search="searchData"
        :methods="{
          viewDashboard,
          searchResultItemLink,
          highlight,
          selectResult,
        }"
      >
        <!-- Search Result Wrapper -->
        <div
          v-if="showResult"
          class="search-result"
          :class="{ 'is-float': !inline, 'ml-6': showHint }"
          :style="{ width: showHint ? '636px' : '100%' }"
        >
          <!-- Search Result Body - list of search result item -->
          <div>
            <div v-if="resultShown.length">
              <div
                v-for="(route, index) in resultShown"
                :key="route.id"
                class="search-result-item-wrapper"
                :class="{ active: index === selectedItemIndex }"
                @mousedown="viewDashboard(route)"
                @mouseover="selectedItemIndex = index"
              >
                <router-link
                  :to="searchResultItemLink(route)"
                  class="search-result-item"
                >
                  <span class="search-result-item-heading has-text-weight-bold">
                    {{ route.search_title }}
                  </span>
                  <breadcrump :item="route" :routes="theRoutes" />
                </router-link>
              </div>
            </div>
            <div v-else class="py-2 px-4">
              <p>Keine Ergebnisse</p>
            </div>
          </div>
          <!-- end of search result body -->

          <!-- Search Result Footer - Show all result control -->
          <div
            v-if="resultShown.length"
            class="p-2"
            style="border-top: 1px solid #ac8856"
          >
            <b-button
              type="is-ghost"
              icon-right="angle-right"
              class="is-flex is-justify-content-space-between is-italic"
              style="font-size: 14px; color: #455a64"
              :label="`Alle Ergebnisse für „${keyword}“ anzeigen`"
              expanded
              @mousedown.native="toSearchPage"
            />
          </div>
          <!-- end of search result footer -->
        </div>
        <!-- End of Search Result Wrapper -->
      </slot>
    </div>
    <!-- End of Search Result -->

    <!-- Search Info -->
    <slot name="info" :search="searchData" />

    <!-- Search Tags -->
    <slot name="top-tag" :search="searchData">
      <div v-if="showTopTags" class="buttons is-justify-content-center mt-4">
        <b-button
          size="is-small"
          v-for="tag in commonTag"
          :key="tag.id"
          :label="tag.name"
          @click="onTopTagClick(tag.name)"
        />
      </div>
    </slot>
    <!-- End of Search Tag -->
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import Fuse from "fuse.js";
import { mapGetters, mapMutations, mapState } from "vuex";
import Breadcrump from "@/components/helper/Breadcrump.vue";

export default {
  name: "SearchBar",

  components: {
    Breadcrump
  },

  props: {
    shadow: {
      type: Boolean,
      default: false
    },
    hideCloseBtn: {
      type: Boolean,
      default: false
    },
    productName: {
      type: String,
      default: "all"
    },
    tag: {
      type: String,
      default: ""
    },
    showTopTags: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: 0
    },
    showHint: {
      type: Boolean,
      default: false
    },
    tooltipPos: {
      type: String,
      default: "is-bottom"
    }
  },

  data() {
    return {
      keyword: "",
      data: {},
      selectedItemIndex: -1,
      resultOpen: false,
      isSearchInfoOpen: false
    };
  },

  computed: {
    ...mapState({
      productRoutes: state => state.product.route.data,
      searchBarFocus: state => state.searchBarFocus
    }),
    ...mapGetters({
      products: "product/data",
      theProduct: "product/dataById",
      routesByProductId: "product/route/dataByParentId",
      navFlatById: "product/navFlat/dataById",
      tags: "tag/data"
    }),
    theRoutes() {
      return this.routesByProductId(this.selectedProduct.id);
    },
    filteredRoutes() {
      const fuse = new Fuse(this.theRoutes, {
        keys: ["tags.name"]
      });

      return fuse
        .search(this.keyword)
        .filter(({ item }) => item.search_title)
        .map(({ item }) => item);
    },
    searchData() {
      return {
        found: this.filteredRoutes,
        keyword: this.keyword,
        product: this.selectedProduct,
        routes: this.theRoutes,
        selectedItemIndex: this.selectedItemIndex
      };
    },
    showResult() {
      return this.resultOpen && this.keyword;
    },
    resultShown() {
      return this.limit
        ? this.filteredRoutes.slice(0, this.limit)
        : this.filteredRoutes;
    },
    selectedProduct: {
      get() {
        return this.theProduct(this.$store.state.menu.selectedProduct);
      },
      set(product) {
        this.setSelectedProductId(product.id);
      }
    },
    commonTag() {
      return this.tags.filter(tag => tag.commonly_searched);
    }
  },

  watch: {
    tag: {
      immediate: true,
      handler(val) {
        this.keyword = val;
      }
    },
    searchBarFocus(newVal) {
      if (newVal) {
        this.focus();
      }
    }
  },

  methods: {
    ...mapMutations({
      setActive: "menu/SET_ACTIVE",
      setRoute: "menu/SET_ROUTE",
      toggleSearchBar: "TOGGLE_SEARCH_BAR",
      toggleSearchBarFocus: "TOGGLE_SEARCH_BAR_FOCUS",
      setSearchBarFocus: "SET_SEARCH_BAR_FOCUS",
      setSelectedProductId: "menu/SET_SELECTED_PRODUCT"
    }),
    convertRouteName(plain) {
      if (!plain) return plain;
      return plain
        .replace(/\s/g, "-")
        .replace(/[^\w-]/g, "")
        .replace(/-+/g, "-");
    },
    toSearchPage() {
      // Prevent user from going to the search page
      // when the search input is empty
      if (!this.keyword) return;

      // If search bar is opened,
      // close it first before going to the search result page
      if (this.$store.state.searchBarOpen) {
        this.toggleSearchBar();
      }

      this.$router
        .push({
          name: "SearchResult",
          query: {
            productName: this.selectedProduct.name,
            tag: this.keyword
          }
        })
        .catch(() => {});
    },
    onSearchEnter() {
      // Do nothing when there is no keyword
      if (!this.keyword) return;

      // Go to Search Page when there is no result selected by arrow key,
      if (this.selectedItemIndex === -1) {
        this.toSearchPage();
      } else {
        // otherwise view the selected result
        this.viewDashboard(this.resultShown[this.selectedItemIndex]);
      }
    },
    searchResultItemLink(route) {
      return {
        name: "View",
        params: {
          productId: this.selectedProduct.id,
          productName: this.convertRouteName(this.selectedProduct.name),
          routeId: route.id,
          routeName: this.convertRouteName(route.name)
        }
      };
    },
    viewDashboard(route) {
      // Take a route object and do all neccessary actions
      // for redirecting user to the selected dashboard

      if (!this.navFlatById(route.id) && !route.name) return;

      // If search bar is opened,
      // close it first before going to the search result page
      if (this.$store.state.searchBarOpen) {
        this.toggleSearchBar();
      }

      this.$router.push(this.searchResultItemLink(route));

      this.setActive(route.id);
      this.setRoute(route.id);

      // close search box
      this.resultOpen = false;
      this.setSearchBarFocus(false);
    },
    selectPrev() {
      const unselected = this.selectedItemIndex === -1;
      if (unselected) return;
      this.selectedItemIndex -= 1;
    },
    selectNext() {
      const onLastItem = this.selectedItemIndex === this.resultShown.length - 1;
      if (onLastItem) return;
      this.selectedItemIndex += 1;
    },
    focus() {
      this.$refs.searchInput.focus();
    },
    onFocus() {
      this.resultOpen = true;
      this.setSearchBarFocus(true);
    },
    onBlur() {
      // Reset search result selection
      this.selectedItemIndex = -1;
      this.resultOpen = false;
      this.setSearchBarFocus(false);
    },
    onInput(val) {
      // Reset search result selection
      this.selectedItemIndex = -1;
      this.highlight(val);
    },
    highlight(val) {
      this.$nextTick(() => {
        const heading = document.querySelector(".search-result-item-heading");
        const breadcrumps = document.querySelectorAll(
          ".search-result-breadcrump span:nth-child(2)"
        );

        if (!heading && breadcrumps) return;

        const regex = new RegExp(val, "gi");
        const openCloseMarkTag = /(<mark class="highlighted">|<\/mark>)/gim;
        const markTag = '<mark class="highlighted">$&</mark>';

        heading.innerHTML = heading.innerHTML.replace(openCloseMarkTag, "");
        heading.innerHTML = heading.innerHTML.replace(regex, markTag);

        breadcrumps.forEach(breadcrump => {
          breadcrump.innerHTML = breadcrump.innerHTML.replace(
            openCloseMarkTag,
            ""
          );
          breadcrump.innerHTML = breadcrump.innerHTML.replace(regex, markTag);
        });
      });
    },
    onTopTagClick(name) {
      this.keyword = name;
      this.onFocus();
    },
    selectResult(index) {
      this.selectedItemIndex = index;
    }
  }
};
</script>

<style>
.searchbar-background {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  background-color: hsla(0, 0%, 4%, 0.86);
}
.searchbar .control.has-icons-left .input {
  padding-left: 3em;
}
.searchbar .control.has-icons-left .icon.is-left {
  left: 5px;
}
.border-bottom-flat .input {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.search-result {
  background-color: white;
  border: 1px solid #ac8856;
  width: 100%;
  position: static;
}
.search-result.is-float {
  position: absolute;
  z-index: 100;
}
.search-result-item-wrapper {
  padding-top: 0.4rem;
  padding-bottom: 0.2rem;
  padding-right: 1rem;
  padding-left: 1rem;
  background-color: white;
}
.search-result-item-wrapper.active {
  background-color: #ac8856;
}
a.search-result-item .search-result-item-heading {
  color: #455a64;
  margin-bottom: -10px;
  display: block;
}
.search-result-item-wrapper.active .search-result-item-heading,
.search-result-item-wrapper.active .search-result-breadcrump {
  color: white;
}

.highlighted {
  font-weight: inherit;
  background-color: #eef43b;
}
</style>
